import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { Observable, Subject, of } from 'rxjs';
import { catchError, take, takeUntil } from 'rxjs/operators';
import { DynamicTableComponent } from '../../dynamic-table/dynamic-table.component';
import { ComponentDataSourceService } from '../../../services/component-data-source/component-data-source.service';
import { FunctionRegistryService } from '../../../services/function-registry/function-registry.service';
import { PermissionEvaluatorService } from '../../../services/component-permission/permission-evaluator.service';
import { EventDTO } from '../../../dto/event/event.dto';

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

@Component({
  selector: 'app-duna-event-users',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    DynamicTableComponent,
  ],
  templateUrl: './duna-event-users.component.html',
  styleUrl: './duna-event-users.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DunaEventUsersComponent implements OnInit, OnDestroy {
  event: EventDTO | null = null;
  eventGuid: string | null = null;
  loading = true;
  loadError: string | null = null;

  private destroy$ = new Subject<void>();
  private canEditEventCache$: Observable<boolean> | null = null;

  readonly participantsTableInput = {
    dataSource: { type: 'text', value: 'event_participants' },
  };
  readonly waitlistTableInput = {
    dataSource: { type: 'text', value: 'event_waitlist' },
  };
  readonly cancellationsTableInput = {
    dataSource: { type: 'text', value: 'event_cancellations' },
  };
  readonly instructorsTableInput = {
    dataSource: { type: 'text', value: 'event_instructors' },
  };

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cdr: ChangeDetectorRef,
    private dataSourceService: ComponentDataSourceService,
    private functionRegistryService: FunctionRegistryService,
    private permissionEvaluatorService: PermissionEvaluatorService
  ) {}

  ngOnInit(): void {
    this.eventGuid = this.getEventGuidFromUrl();
    if (!this.eventGuid) {
      this.loading = false;
      this.loadError = 'Kunne ikke finde event i URL';
      this.cdr.markForCheck();
      return;
    }
    this.loadEvent();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get showWaitlistSection(): boolean {
    return this.event?.wait_list_type !== 'no_wait_list';
  }

  canEditEvent(): Observable<boolean> {
    if (!this.event?.guid) {
      return of(false);
    }
    if (this.canEditEventCache$) {
      return this.canEditEventCache$;
    }
    const contextTemplate = `{
      "eventId": "${this.event.guid}",
      "eventOrganizationId": ${this.event.organization?.id || 'null'},
      "eventLocationGuid": "${this.event.predefined_location?.guid || ''}"
    }`;
    this.canEditEventCache$ = this.permissionEvaluatorService
      .evaluatePermission(
        'canEditEvent',
        contextTemplate,
        {
          eventId: this.event.guid,
          eventOrganizationId: this.event.organization?.id,
          eventLocationGuid: this.event.predefined_location?.guid,
        },
        this.route
      )
      .pipe(take(1));
    return this.canEditEventCache$;
  }

  onMailParticipants(): void {
    this.functionRegistryService.executeFunction('mailtoEventParticipants');
  }

  onCopyTemplate(): void {
    this.functionRegistryService.executeFunction('openModal', 'edit_template');
  }

  onAddUsers(): void {
    this.functionRegistryService.executeFunction('openModal', 'add_event_users');
  }

  private loadEvent(): void {
    const dataSource = this.dataSourceService.getDataSource('event');
    if (!dataSource) {
      this.loading = false;
      this.loadError = 'Event data source ikke tilgængelig';
      this.cdr.markForCheck();
      return;
    }

    dataSource
      .execute()
      .pipe(catchError(() => of(null)), takeUntil(this.destroy$))
      .subscribe({
        next: (data: EventDTO | null) => {
          if (data) {
            this.event = data;
            this.canEditEventCache$ = null;
            this.loadError = null;
          } else if (!this.event) {
            this.loadError = 'Kunne ikke indlæse event';
          }
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: () => {
          this.loading = false;
          this.loadError = 'Kunne ikke indlæse event';
          this.cdr.markForCheck();
        },
      });
  }

  private getEventGuidFromUrl(): string | null {
    const url = (this.router.url || '').split('?')[0];
    const segments = url.split('/');
    const found = segments.find((s) => UUID_REGEX.test(s));
    return found || null;
  }
}
